import React, { useEffect, useRef, useState } from "react";
import { useScrubEngine, decodeFramesFromUrls } from "@/lib/scrubEngine";
import { useScrubVideoPresentation } from "@/lib/useScrubVideoPresentation";
import { afterPaint, afterVideoFrame } from "@/lib/mediaPaint";
import { captureVideoFrame, releaseCanvasFrame } from "@/lib/videoFrame";
import { MAX_PREPARED_TRANSITION_SECONDS, resolveTransitionPlayback } from "@/lib/transitionPlayback";
import { MEDIA_VIGNETTE } from "../lib/mediaPresentation";
import { recommendScrubMode, isIOS, isSafari } from "@/lib/device";
import { isShowroomActive } from "@/lib/showroom";
import { createTransitionMachine } from "@/lib/scrub/transitionMachine";
import { FALLBACK_REASONS, perfRecord, recordPrewarm } from "@/lib/perfLog";
import LetterboxBackdrop from "./LetterboxBackdrop";

/**
 * ScrubOverlay — renders a transition video the user can scrub.
 *
 * Lifecycle is driven by an explicit finite-state machine (see
 * `lib/scrub/transitionMachine.js`). Critical iter28 robustness fixes:
 *   • Event listeners are now attached BEFORE we call v.load(), and we
 *     also synchronously short-circuit when v.readyState already >= 1
 *     (HAVE_METADATA). Previously the v.load() call could synchronously
 *     fire loadedmetadata from the byte cache BEFORE our addEventListener
 *     ran — leaving the FSM stuck in `arming` until timeout and triggering
 *     a false `transition_not_ready` fade.
 *   • We avoid forcing v.load() when the element already has the same
 *     src + readyState >= HAVE_FUTURE_DATA — saves a frame of teardown on
 *     repeated A→B cycles.
 *   • Adaptive arming timeout (1500 ms desktop, 2500 ms iOS/Safari/mobile)
 *     so the first transition on iPad over cellular doesn't randomly fade.
 *   • Every `failed`/timeout path now records a rich fallback details
 *     payload (readyState, networkState, currentTime, duration, cached,
 *     firstUse) so QA can tell a real timeout from a missed event.
 */
function getArmingTimeoutMs() {
  // iOS Safari, including Home-Screen PWA mode, needs the longer budget —
  // first-load metadata fetches over LTE/5G can exceed 1.5 s.
  if (typeof window === "undefined") return 1800;
  if (isIOS() || isSafari()) return 2500;
  // Touch devices on slower CPUs (mid-range Android, older iPads via the
  // Safari sniff above) also benefit from the longer budget.
  if ("ontouchstart" in window) return 2200;
  return 1500;
}
const ARMING_TIMEOUT_MS = getArmingTimeoutMs();

function axisLabel(d) {
  return { left: "left", right: "right", up: "up", down: "down", none: "" }[d] || "";
}

export default function ScrubOverlay({
  transition,
  fromScene,
  toScene,
  reverse = false,
  project,
  backdropManaged = false,
  controlledProgress = null,
  releaseSignal = null,
  autoTween = false,        // NEW (iter27): when true, ignore user gesture
                            // and tween 0→1 (forward) or 1→0 already-reversed
                            // at the video's natural playback rate. Used for
                            // `transition_type='auto_video'`.
  onComplete,
  onCancel,
  onFailedFallback,   // (reason: FALLBACK_REASONS) => void
}) {
  const direction = transition.swipe_direction === "right" || transition.swipe_direction === "down" ? -1 : 1;
  const axis = (transition.swipe_direction === "up" || transition.swipe_direction === "down") ? "y" : "x";

  const mode = recommendScrubMode();
  const playback = resolveTransitionPlayback(transition, reverse);
  const videoUrl = playback.url;
  const mediaReverse = playback.reverse;
  const controlled = controlledProgress !== null;

  // ── Pre-baked image-sequence canvas mode ─────────────────────────────
  // When the entire project has been downloaded for Showroom mode,
  // scrub through canvas-blitted ImageBitmaps instead of video.currentTime —
  // zero video decoder cost, deterministic frames. Falls back to video mode
  // if the frames fail to load; the video path itself falls back to fade.
  const prebakedUrls = transition?.image_sequence_urls?.length ? transition.image_sequence_urls : null;
  const [canvasFrames, setCanvasFrames] = useState(null);
  const [canvasFailed, setCanvasFailed] = useState(false);
  // Online iOS uses the same native video scrub engine as other devices.
  // Loading every pre-baked frame first used to block a single swipe on
  // dozens of simultaneous network requests, even with Showroom disabled.
  const wantCanvas = !!prebakedUrls && isShowroomActive();
  const effectiveMode = wantCanvas && !canvasFailed ? "canvas" : mode;
  // A prepared reverse clip already contains the reversed motion and plays
  // forward. The original image sequence still needs its logical reversal.
  const nativeAuto = autoTween && effectiveMode === "video" && !mediaReverse;
  const [nativeProgress, setNativeProgress] = useState(0);
  const [nativePlaying, setNativePlaying] = useState(false);
  const endpointHostRef = useRef(null);

  const { videoRef, canvasRef, surfaceProps, progress, scrubbing, setProgress, awaitSettled } = useScrubEngine({
    axis, direction,
    mode: effectiveMode, frames: canvasFrames,
    reverse: effectiveMode === "canvas" ? reverse : mediaReverse,
    onComplete, onCancel,
    enabled: !controlled && !autoTween,
  });

  // ── Deterministic FSM ────────────────────────────────────────────────
  // `fsmState` is mirrored to React state so the debug HUD can show it.
  const [fsmState, setFsmState] = useState("idle");
  const machineRef = useRef(null);
  if (!machineRef.current) {
    machineRef.current = createTransitionMachine((next) => {
      setFsmState(next);
      // eslint-disable-next-line no-console
      console.info(`[transitionFSM] → ${next}`);
    });
  }
  const machine = machineRef.current;

  // Buffer the most-recent controlledProgress so we can apply it the
  // moment the video becomes ready (without losing the user's drag
  // distance during the arming window).
  const pendingProgressRef = useRef(0);
  const tArmStartRef = useRef(0);
  const completedRef = useRef(false);
  // True if the prewarm hit fired before we mounted (debug field).
  const cachedRef = useRef(false);
  // True after the first armed → ready/failed cycle for this transition.
  const alreadyArmedOnceRef = useRef(false);
  const pausedPresentation = effectiveMode === "video" && !nativeAuto;
  const frameFailedRef = useRef(false);
  const { canvasRef: presentationCanvasRef, frameReady } = useScrubVideoPresentation({
    videoRef, enabled: pausedPresentation,
    onFrameError: error => {
      if (frameFailedRef.current) return;
      frameFailedRef.current = true;
      onFailedFallback?.(FALLBACK_REASONS.PLAYBACK_ERROR, {
        transitionId: transition.id, reverse, source: "canvas_capture_error",
        message: error?.message || "",
      });
    },
  });

  // ── Mount: arm canvas mode (pre-baked frames) ────────────────────────
  useEffect(() => {
    if (effectiveMode !== "canvas") return undefined;
    let alive = true;
    let loadedFrames = [];
    const controller = new AbortController();
    machine.go("arming", { transitionId: transition.id, reverse, mode: "canvas" });
    tArmStartRef.current = performance.now();
    const timer = setTimeout(() => controller.abort(), ARMING_TIMEOUT_MS);
    decodeFramesFromUrls(prebakedUrls, { signal: controller.signal }).then(({ ok, frames, reason }) => {
      clearTimeout(timer);
      if (!alive) { frames.forEach(frame => frame.close?.()); return; }
      if (ok && frames.length) {
        loadedFrames = frames;
        setCanvasFrames(frames);
        machine.go("ready", { frames: frames.length, armingMs: performance.now() - tArmStartRef.current });
        perfRecord(`transition:${transition.id}:armed`, performance.now() - tArmStartRef.current, {
          mode: "canvas", frames: frames.length,
        });
        setProgress(pendingProgressRef.current);
      } else {
        // eslint-disable-next-line no-console
        console.warn("[scrub] prebaked frames failed → video fallback:", reason);
        machine.reset();
        setCanvasFailed(true);
      }
    });
    return () => {
      alive = false;
      clearTimeout(timer);
      controller.abort();
      loadedFrames.forEach(frame => frame.close?.());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transition?.id, reverse, effectiveMode]);

  // Debug-only breadcrumb consumed by the zone-alignment snapshot.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.__lastScrubDebug = {
      transitionId: transition?.id || null,
      mode: effectiveMode,
      usedPrebaked: effectiveMode === "canvas",
      prebakedAvailable: !!prebakedUrls,
      frames: canvasFrames?.length || 0,
    };
  }, [transition?.id, effectiveMode, canvasFrames, prebakedUrls]);

  // Capture the mounted element now: React detaches its ref before passive
  // cleanup. Release it on exit or when canvas replaces the video, including
  // a video mounted later after a failed canvas attempt.
  useEffect(() => {
    if (effectiveMode !== "video") return undefined;
    const v = videoRef.current;
    if (!v) return undefined;
    return () => {
      try { v.pause(); v.removeAttribute("src"); v.load(); } catch (_) { /* ignore */ }
    };
  }, [effectiveMode, videoRef]);

  // ── Mount: arm the video ─────────────────────────────────────────────
  useEffect(() => {
    if (effectiveMode !== "video") return undefined;
    // Defensive: only arm if the transition has media we can scrub.
    if (!videoUrl) {
      onFailedFallback?.(FALLBACK_REASONS.MISSING_TRANSITION_MEDIA, {
        transitionId: transition?.id, reverse,
      });
      return undefined;
    }
    if (!transition.scrubbable_enabled && transition.transition_type !== "auto_video") {
      onFailedFallback?.(FALLBACK_REASONS.UNSUPPORTED_SCRUB, {
        transitionId: transition.id, reverse,
        transition_type: transition.transition_type,
        scrubbable_enabled: transition.scrubbable_enabled,
      });
      return undefined;
    }
    machine.go("arming", { transitionId: transition.id, reverse });
    tArmStartRef.current = performance.now();
    const v = videoRef.current;
    if (!v) return undefined;

    const onMeta = () => {
      // Reset to the correct boundary frame for the direction we're
      // playing — forward starts at t=0, reverse starts at duration.
      try {
        const dur = isFinite(v.duration) ? v.duration : 0;
        v.currentTime = mediaReverse ? Math.max(0, dur * 0.999) : 0;
      } catch (_) { /* ignore */ }
      machine.go("ready", {
        duration: v.duration, videoWidth: v.videoWidth, videoHeight: v.videoHeight,
        armingMs: performance.now() - tArmStartRef.current,
      });
      perfRecord(`transition:${transition.id}:armed`, performance.now() - tArmStartRef.current, {
        duration: v.duration, readyState: v.readyState, cached: cachedRef.current,
      });
      // Native auto playback must not enqueue a seek that can rewind it
      // after play() starts. Drag/reverse paths still apply buffered input.
      if (!nativeAuto) setProgress(pendingProgressRef.current);
    };
    const onErr = () => {
      if (machine.state === "failed" || machine.state === "committed") return;
      const code = v.error?.code;
      const message = v.error?.message || "";
      machine.go("failed", { code, message });
      onFailedFallback?.(FALLBACK_REASONS.PLAYBACK_ERROR, {
        transitionId: transition.id, reverse, code, message,
        readyState: v.readyState, networkState: v.networkState,
        currentTime: v.currentTime, duration: v.duration,
        cached: cachedRef.current, firstUse: !alreadyArmedOnceRef.current,
      });
    };

    // CRITICAL iter28: attach listeners BEFORE any load()-triggered event
    // can fire, otherwise metadata-from-cache races us and we miss the
    // event entirely → FSM stuck in `arming` → false transition_not_ready.
    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("error", onErr);

    // Decide whether we actually need to force a reload. The byte cache
    // is shared across <video> elements with the same src — if the
    // element is brand-new (readyState 0) it needs load(); if the parent
    // reused a DOM node that already has metadata, we skip.
    const srcIsSet = (v.currentSrc || v.src) && (v.currentSrc || v.src).includes(
      videoUrl
    );
    const alreadyLoaded = srcIsSet && v.readyState >= 1 /* HAVE_METADATA */;
    cachedRef.current = alreadyLoaded;
    if (!alreadyLoaded) {
      try {
        v.pause();
        v.currentTime = 0;
        v.load();
      } catch (_) { /* ignore */ }
    }

    // Immediate synchronous short-circuit — if metadata is already there
    // (cached prewarm hit) the loadedmetadata event WON'T fire again, so
    // we have to fire onMeta ourselves.
    if (v.readyState >= 1 /* HAVE_METADATA */) {
      onMeta();
    }

    // Arming watchdog — bytes are pre-warmed by useScenePrewarm; if even
    // a cached load can't produce metadata in this window, something is
    // wrong (CORS, codec, ingress redirect) and we must fall back.
    const armTimer = setTimeout(() => {
      if (machine.state === "arming") {
        machine.go("failed", { reason: "arm_timeout" });
        recordPrewarm(transition.id, "miss", performance.now() - tArmStartRef.current, {
          reason: "arm_timeout",
        });
        onFailedFallback?.(FALLBACK_REASONS.TRANSITION_NOT_READY, {
          transitionId: transition.id, reverse,
          readyState: v.readyState, networkState: v.networkState,
          currentTime: v.currentTime, duration: v.duration,
          cached: cachedRef.current, firstUse: !alreadyArmedOnceRef.current,
          timeoutMs: ARMING_TIMEOUT_MS,
        });
      }
    }, ARMING_TIMEOUT_MS);

    alreadyArmedOnceRef.current = true;
    return () => {
      clearTimeout(armTimer);
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("error", onErr);
      // Pause without rewinding: iOS can present a queued boundary frame
      // while its native layer is being removed during the handoff.
      try { v.pause(); } catch (_) { /* ignore */ }
      machine.reset();
    };
    // Re-arm when transition identity / reverse / mode changes — the inner
    // deps (videoRef.current etc.) are stable refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transition?.id, videoUrl, mediaReverse, reverse, effectiveMode, nativeAuto]);

  // ── Push controlled progress into the engine on every change ─────────
  useEffect(() => {
    if (!controlled || autoTween) return;
    pendingProgressRef.current = controlledProgress;
    if (machine.state === "ready" || machine.state === "scrubbing") {
      // First non-zero progress promotes us from ready → scrubbing.
      if (machine.state === "ready" && controlledProgress > 0.001) {
        machine.go("scrubbing");
      }
      setProgress(controlledProgress);
    }
    // While arming, we buffer the value but DO NOT log fallback — the
    // arming window handles its own timeout.
  }, [controlled, controlledProgress, setProgress, machine]);

  // Forward automatic transitions are ordinary video playback. Repeated
  // currentTime assignments make every frame a random-access decode and can
  // leave long-GOP videos visibly frozen while the wall-clock tween advances.
  useEffect(() => {
    if (!nativeAuto || fsmState !== "ready" || machine.state !== "ready") return undefined;
    const v = videoRef.current;
    if (!v) return undefined;
    let cancelled = false;
    let finished = false;
    let stallTimer = null;
    let cancelReveal = null;
    let cancelEndpointPaint = null;
    let endpoint = null;
    let endpointTransferred = false;
    let deliveringEndpoint = false;
    let lastTime = v.currentTime;
    setNativeProgress(0);
    setNativePlaying(false);

    const fail = (source, error) => {
      if (cancelled || finished) return;
      finished = true;
      clearTimeout(stallTimer);
      cancelReveal?.();
      cancelEndpointPaint?.();
      setNativePlaying(false);
      try { v.pause(); } catch (_) { /* ignore */ }
      machine.go("failed", { source });
      onFailedFallback?.(FALLBACK_REASONS.PLAYBACK_ERROR, {
        transitionId: transition.id, reverse, source,
        message: error?.message || "",
        readyState: v.readyState, networkState: v.networkState,
        currentTime: v.currentTime, duration: v.duration,
      });
    };
    const armStallTimer = () => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(() => fail("native_play_stalled"), 2500);
    };
    const updateProgress = () => {
      if (cancelled || finished) return;
      const time = v.currentTime;
      if (Number.isFinite(v.duration) && v.duration > 0) {
        setNativeProgress(Math.max(0, Math.min(1, time / v.duration)));
      }
      // Read media time; never route native playback back through setProgress,
      // which writes currentTime. A frozen player cannot reset this deadline.
      if (Math.abs(time - lastTime) > 0.01) {
        lastTime = time;
        armStallTimer();
      }
    };
    const onPlaying = () => {
      if (cancelled || finished || v.paused || v.readyState < 2) return;
      cancelReveal?.();
      cancelReveal = afterVideoFrame(v, () => {
        if (!cancelled && !finished && !v.paused && v.readyState >= 2) setNativePlaying(true);
      });
      updateProgress();
    };
    const onPause = () => {
      // Some browsers queue pause just before ended becomes observable.
      // Do not expose the old scene during that final-frame boundary.
      const atEnd = v.ended || (Number.isFinite(v.duration) && v.duration > 0
        && v.currentTime >= v.duration - 1 / 30);
      if (!cancelled && !finished && !atEnd) {
        cancelReveal?.();
        setNativePlaying(false);
      }
    };
    const onEnded = () => {
      if (cancelled || finished || !v.ended) return;
      endpoint = captureVideoFrame(v);
      if (!endpoint || !endpointHostRef.current) {
        releaseCanvasFrame(endpoint);
        endpoint = null;
        fail("endpoint_capture_error");
        return;
      }
      finished = true;
      clearTimeout(stallTimer);
      cancelReveal?.();
      setNativeProgress(1);
      endpoint.className = v.className;
      endpoint.dataset.testid = "viewer-scrub-endpoint";
      endpointHostRef.current.appendChild(endpoint);
      // Paint the bounded endpoint above the still-mounted native layer
      // BEFORE decoder teardown. The parent adopts this very same canvas.
      cancelEndpointPaint = afterPaint(() => {
        if (cancelled) return;
        deliveringEndpoint = true;
        try { endpointTransferred = onComplete?.(endpoint) === true; }
        finally {
          deliveringEndpoint = false;
          if (cancelled && !endpointTransferred) releaseCanvasFrame(endpoint);
        }
        if (!cancelled) {
          machine.go("committing");
          machine.go("committed");
        }
      });
    };
    v.addEventListener("playing", onPlaying);
    v.addEventListener("pause", onPause);
    v.addEventListener("timeupdate", updateProgress);
    v.addEventListener("ended", onEnded);
    armStallTimer();
    const originalRate = v.playbackRate;
    try {
      // Preserve the authored camera speed and its normal decoding workload.
      v.playbackRate = 1;
      const playback = v.play();
      playback?.catch(error => fail("native_play_rejected", error));
    } catch (error) {
      fail("native_play_rejected", error);
    }
    return () => {
      cancelled = true;
      clearTimeout(stallTimer);
      cancelReveal?.();
      cancelEndpointPaint?.();
      if (!endpointTransferred && !deliveringEndpoint) releaseCanvasFrame(endpoint);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("timeupdate", updateProgress);
      v.removeEventListener("ended", onEnded);
      try { v.playbackRate = originalRate; v.pause(); } catch (_) { /* ignore */ }
    };
    // Progress renders must not restart playback; identity changes cancel it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nativeAuto, fsmState, transition?.id, videoUrl]);

  // ── Auto-tween (transition_type='auto_video') ───────────────────────
  // Once the FSM hits `ready`, drive progress from 0→1 at the video's
  // natural duration (or a sensible fallback). On finish → onComplete.
  // The reverse direction is handled inside the scrubEngine — we always
  // tween 0→1 here, and reverse=true maps that to mediaP = 1−p internally.
  //
  // IMPORTANT: this effect must NOT call any `machine.go` that changes
  // the *observed* fsmState during the tween, otherwise its own deps
  // change → React cleans up the effect → cancelled=true → tween dies
  // half-way. The machine still transitions, but only AFTER the tween
  // finishes (committing → committed).
  const autoTweenRanRef = useRef(false);
  useEffect(() => {
    if (!autoTween || nativeAuto) return undefined;
    if (autoTweenRanRef.current) return undefined;
    if (fsmState !== "ready") return undefined;
    autoTweenRanRef.current = true;
    const v = videoRef.current;
    const durationMs = (effectiveMode === "canvas"
      ? Math.min(MAX_PREPARED_TRANSITION_SECONDS, playback.duration || MAX_PREPARED_TRANSITION_SECONDS)
      : (v?.duration && isFinite(v.duration) ? v.duration : 2.5)) * 1000;
    const t0 = performance.now();
    let cancelled = false;
    const tick = (t) => {
      if (cancelled) return;
      const k = Math.min(1, (t - t0) / Math.max(800, durationMs));
      setProgress(k);
      if (k < 1) requestAnimationFrame(tick);
      else {
        // The final seek may still be decoding after the tween has ended.
        // Keep the overlay until that frame settles (with a bounded wait)
        // so the parent captures the final frame for the handoff.
        awaitSettled(400).then(settled => {
          if (cancelled) return;
          if (!settled) {
            machine.go("failed", { source: "final_seek_timeout" });
            onFailedFallback?.(FALLBACK_REASONS.TRANSITION_NOT_READY, {
              transitionId: transition.id, reverse, source: "final_seek_timeout",
            });
            return;
          }
          machine.go("committing");
          machine.go("committed");
          onComplete?.();
        });
      }
    };
    requestAnimationFrame(tick);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTween, nativeAuto, fsmState]);

  // ── Release signal → committing or cancelling ────────────────────────
  const releaseRef = useRef(null);
  useEffect(() => {
    if (autoTween) return undefined;  // auto-tween fires its own onComplete
    if (!releaseSignal || releaseRef.current === releaseSignal) return undefined;
    releaseRef.current = releaseSignal;
    // If the FSM never reached `ready`, we can't deliver a real scrub —
    // signal fallback so the parent does a fade (only if the user wanted
    // to commit; a cancel can just reset).
    if (machine.state === "arming") {
      if (releaseSignal.commit) {
        const v = videoRef.current;
        onFailedFallback?.(FALLBACK_REASONS.TRANSITION_NOT_READY, {
          transitionId: transition.id, reverse,
          readyState: v?.readyState, networkState: v?.networkState,
          currentTime: v?.currentTime, duration: v?.duration,
          cached: cachedRef.current, firstUse: !alreadyArmedOnceRef.current,
          source: "release_while_arming",
        });
      }
      else onCancel?.();
      return undefined;
    }
    const target = releaseSignal.commit ? 1 : 0;
    machine.go(releaseSignal.commit ? "committing" : "cancelling");
    const start = controlledProgress ?? 0;
    const t0 = performance.now();
    let cancelled = false;
    const tick = (t) => {
      if (cancelled) return;
      const k = Math.min(1, (t - t0) / 280);
      const eased = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      const p = start + (target - start) * eased;
      setProgress(p);
      if (k < 1) requestAnimationFrame(tick);
      else {
        awaitSettled(400).then(settled => {
          if (cancelled) return;
          completedRef.current = true;
          if (releaseSignal.commit && !settled) {
            machine.go("failed", { source: "final_seek_timeout" });
            onFailedFallback?.(FALLBACK_REASONS.TRANSITION_NOT_READY, {
              transitionId: transition.id, reverse, source: "final_seek_timeout",
            });
          } else if (releaseSignal.commit) {
            machine.go("committed");
            onComplete?.();
          } else {
            machine.go("cancelled");
            onCancel?.();
          }
        });
      }
    };
    requestAnimationFrame(tick);
    return () => { cancelled = true; };
    // Intentional: re-run ONLY when the release token changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseSignal]);

  // Hold the source poster until an actual decoded transition frame exists.
  // Time-based poster blending can reveal the destination before a slow
  // reverse seek has produced even its first frame, then jump backwards.
  const fromPoster = (fromScene && (fromScene.poster_url || (fromScene.type === "static_image" && fromScene.media_url))) || null;
  // Match the foreground scenes' fit so the rotate transition doesn't
  // visually toggle between cropped and letterboxed.
  const projFit = project?.viewer_fit_mode;
  const effectiveFit = (projFit === "cover" || projFit === "contain")
    ? projFit
    : (fromScene?.display_mode || toScene?.display_mode || "contain");
  const fitClass = effectiveFit === "cover" ? "object-cover" : "object-contain";
  const displayedProgress = nativeAuto ? nativeProgress : progress;

  return (
    <div
      className={`absolute inset-0 ${backdropManaged ? "z-10" : "z-40 bg-black"}`}
      {...(controlled ? { style: { pointerEvents: "none" } } : surfaceProps)}
      data-testid="viewer-scrub-overlay"
      data-fsm-state={fsmState}
      data-transition-id={transition?.id || ""}
    >
      {!backdropManaged && <LetterboxBackdrop scene={fromScene} project={project} />}
      {fromPoster && !backdropManaged && (
        <img src={fromPoster} alt="" aria-hidden="true" draggable={false}
          className={`absolute inset-0 w-full h-full ${fitClass} pointer-events-none`} />
      )}
      {effectiveMode === "video" && (
        <video
          ref={videoRef}
          src={videoUrl}
          className={`absolute inset-0 w-full h-full ${fitClass}`}
          style={{ visibility: nativeAuto && nativePlaying ? "visible" : "hidden" }}
          playsInline muted preload="auto"
          data-testid="viewer-scrub-video"
        />
      )}
      {pausedPresentation && (
        <canvas ref={presentationCanvasRef}
          className={`absolute inset-0 w-full h-full ${fitClass}`}
          style={{ visibility: frameReady ? "visible" : "hidden" }}
          data-testid="viewer-scrub-canvas" data-frame-ready={frameReady} />
      )}
      {effectiveMode === "canvas" && (
        <canvas ref={canvasRef} className={`absolute inset-0 w-full h-full ${fitClass}`} data-testid="viewer-scrub-canvas" />
      )}
      {nativeAuto && <div ref={endpointHostRef} className="absolute inset-0 pointer-events-none" />}
      {!backdropManaged && (
        <div className="absolute inset-0 pointer-events-none" data-testid="viewer-scrub-vignette" style={{
          background: MEDIA_VIGNETTE
        }} />
      )}
      <div className="absolute top-0 inset-x-0 h-0.5 bg-white/10 pointer-events-none">
        <div className="h-full bg-white/80" style={{ width: `${(displayedProgress * 100).toFixed(1)}%`, transition: scrubbing ? "none" : "width 200ms ease" }} />
      </div>
      {!autoTween && !scrubbing && progress < 0.02 && (
        <div className="absolute inset-x-0 bottom-12 text-center text-white/80 text-xs uppercase tracking-[0.3em] pointer-events-none">
          Drag to move {axisLabel(transition.swipe_direction)} · release to return
        </div>
      )}
    </div>
  );
}

// Re-export so PublicViewer can build matching fallback reasons.
export { FALLBACK_REASONS };
